import { useState } from "react";
import { HugeiconsIcon } from "@hugeicons/react";
import { InformationCircleIcon } from "@hugeicons-pro/core-stroke-rounded";
import { Show } from "shared/components";
import { Card } from "shared/components/Card";
import { Loader } from "shared/components/Loader";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "shared/components/Tooltip";
import { cx } from "shared/utils";
import type {
  LeasingFunnelResponse,
  LeasingFunnelStep
} from "api/crm-analytics";
import { AnalyticsEmptyState } from "../AnalyticsEmptyState";
import {
  TOOLTIP_LABEL,
  TOOLTIP_SUBTITLE,
  TOOLTIP_SURFACE,
  TOOLTIP_TITLE
} from "../ChartTooltip";
import { useChartWidth } from "../modules/useChartWidth";
import { FunnelBands } from "./FunnelBands";

const ROW_HEIGHT = 48;
const MIN_BANDS_WIDTH = 120;
const MAX_BANDS_WIDTH = 640;

const HELP_TEXT =
  "Only leads created in the selected date range are counted, and each lead " +
  "is counted once — at the furthest step it has ever reached, even if it got " +
  "there after the range ended. A step therefore shows every lead that reached " +
  "it or went further, which is why the funnel always narrows.";

const EMPTY_DESCRIPTION = "No leads were created in this date range";

// The help bubble behind the info icon is prose, so it keeps its own wider,
// wrapping surface. The per-step tooltip uses the shared one instead, which is
// nowrap and sized for short lines.
const HELP_TOOLTIP_CLASSNAME =
  "max-w-[280px] rounded-lg bg-alpha-800 p-3 text-sm text-white z-[14]";

const formatCount = (count: number) => count.toLocaleString("en-US");

// Whole percents read better at this size, but a step that kept a few leads
// must not round down to a flat 0%.
const formatPercent = (value: number | null) => {
  if (value === null) return "--";
  if (value > 0 && value < 1) return "<1%";
  return `${Math.round(value)}%`;
};

const leadNoun = (count: number) => (count === 1 ? "lead" : "leads");

const StepTooltipBody = ({
  step,
  total
}: {
  step: LeasingFunnelStep;
  total: number;
}) => (
  <>
    <p className={TOOLTIP_TITLE}>{step.label}</p>
    {/* The count is the headline, not another detail line — same split as the
        Sankey and the bar charts. */}
    <p className={TOOLTIP_SUBTITLE}>
      {formatCount(step.count)} {leadNoun(step.count)}
    </p>
    <Show if={step.conversion_from_previous !== null}>
      <p className={TOOLTIP_LABEL}>
        {formatPercent(step.conversion_from_previous)} of previous step
      </p>
    </Show>
    <Show if={step.dropped_from_previous !== null}>
      <p className={TOOLTIP_LABEL}>
        {formatCount(step.dropped_from_previous ?? 0)} did not reach this step
      </p>
    </Show>
    <Show if={total > 0}>
      <p className={TOOLTIP_LABEL}>
        {formatPercent(step.share_of_total)} of all leads created
      </p>
    </Show>
  </>
);

const RatesSummary = ({ rates }: { rates: LeasingFunnelResponse["rates"] }) => (
  <div className="flex gap-6">
    <div className="flex flex-col gap-0.5 text-right">
      <span className="text-xs font-medium text-content-300 whitespace-nowrap">
        Lead to application
      </span>
      <span className="text-xl font-bold text-content-1000">
        {formatPercent(rates?.lead_to_application ?? null)}
      </span>
    </div>
    <div className="flex flex-col gap-0.5 text-right">
      <span className="text-xs font-medium text-content-300 whitespace-nowrap">
        Lead to approval
      </span>
      <span className="text-xl font-bold text-content-1000">
        {formatPercent(rates?.lead_to_approval ?? null)}
      </span>
    </div>
  </div>
);

export const LeasingFunnelCard = ({
  data,
  isLoading,
  dateLabel,
  onStepClick,
  reserveActionGutter = false
}: {
  data: LeasingFunnelResponse | undefined;
  isLoading: boolean;
  dateLabel: string;
  onStepClick?: (step: LeasingFunnelStep) => void;
  /** Leave room for a dashboard grid's delete button over the top-right corner. */
  reserveActionGutter?: boolean;
}) => {
  const [hoveredKey, setHoveredKey] = useState<string | null>(null);
  const { ref: bandsRef, width: measuredWidth } = useChartWidth();

  const steps = data?.steps ?? [];
  const total = data?.total_leads_created ?? 0;
  const hasData = total > 0;
  const showEmptyState = !isLoading && !hasData;
  const showFunnel = !isLoading && hasData;
  const bandsWidth = Math.max(
    MIN_BANDS_WIDTH,
    Math.min(measuredWidth, MAX_BANDS_WIDTH)
  );

  return (
    <Card className="h-full p-6 w-full">
      <div
        className={cx(
          "flex items-start justify-between gap-4",
          reserveActionGutter && "pr-10"
        )}
      >
        <div className="space-y-1">
          <div className="flex items-center gap-1.5">
            <p className="text-base font-medium text-content-700">
              Leasing funnel
            </p>
            <Tooltip placement="right-start">
              <TooltipTrigger asChild>
                <span className="text-content-300 cursor-pointer">
                  <HugeiconsIcon icon={InformationCircleIcon} size={16} />
                </span>
              </TooltipTrigger>
              <TooltipContent className={cx(HELP_TOOLTIP_CLASSNAME, "p-4")}>
                {HELP_TEXT}
              </TooltipContent>
            </Tooltip>
          </div>
          <p className="text-sm font-medium text-content-300">{dateLabel}</p>
        </div>
        <Show if={showFunnel && Boolean(data?.rates)}>
          <RatesSummary rates={data?.rates ?? null} />
        </Show>
      </div>

      <Show if={isLoading}>
        <div className="flex items-center justify-center min-h-[200px]">
          <Loader size={24} />
        </div>
      </Show>

      <Show if={showEmptyState}>
        <AnalyticsEmptyState description={EMPTY_DESCRIPTION} className="py-6" />
      </Show>

      <Show if={showFunnel}>
        <div className="relative mt-4 max-w-[1040px]">
          <div className="grid grid-cols-[minmax(0,130px)_minmax(0,1fr)_minmax(0,150px)] gap-3 xl:grid-cols-[minmax(0,200px)_minmax(0,1fr)_minmax(0,220px)] xl:gap-8">
            <div>
              {steps.map((step) => (
                <div
                  key={step.key}
                  className="flex items-center"
                  style={{ height: ROW_HEIGHT }}
                >
                  <span className="truncate text-sm font-medium text-content-1000">
                    {step.label}
                  </span>
                </div>
              ))}
            </div>

            <div ref={bandsRef} className="flex justify-center">
              <FunnelBands
                steps={steps}
                total={total}
                width={bandsWidth}
                rowHeight={ROW_HEIGHT}
                hoveredKey={hoveredKey}
              />
            </div>

            <div>
              {steps.map((step) => {
                const isBiggestDrop = step.key === data?.biggest_drop_key;
                return (
                  <div
                    key={step.key}
                    className="flex items-center gap-2"
                    style={{ height: ROW_HEIGHT }}
                  >
                    <span className="min-w-[36px] text-sm font-bold text-content-1000">
                      {formatCount(step.count)}
                    </span>
                    <span
                      className={cx(
                        "truncate text-xs font-medium",
                        isBiggestDrop ? "text-primary-500" : "text-content-300"
                      )}
                    >
                      {step.conversion_from_previous === null
                        ? "100%"
                        : `${formatPercent(step.conversion_from_previous)} of previous`}
                    </span>
                    <Show if={isBiggestDrop}>
                      <span className="hidden shrink-0 rounded-full bg-primary-20 px-2 py-0.5 text-xs font-medium text-primary-500 xl:inline">
                        Biggest drop
                      </span>
                    </Show>
                  </div>
                );
              })}
            </div>
          </div>

          {steps.map((step, index) => (
            <Tooltip
              key={step.key}
              placement="right"
              open={hoveredKey === step.key}
              onOpenChange={(open) => setHoveredKey(open ? step.key : null)}
              // The trigger is a row-sized overlay on a page that scrolls. A
              // wheel scroll can move the row out from under a pointer that
              // never itself moves, so leaving is not guaranteed to be what
              // closes this — scrolling the page away from the funnel closes
              // it too, rather than leaving a tooltip behind on screen.
              dismissOptions={{ ancestorScroll: true }}
            >
              <TooltipTrigger asChild>
                <button
                  type="button"
                  aria-label={`${step.label}: ${formatCount(step.count)} ${leadNoun(step.count)}. Show leads`}
                  className={cx(
                    "absolute inset-x-0 rounded-lg hover:bg-alpha-20",
                    onStepClick ? "cursor-pointer" : "cursor-default"
                  )}
                  style={{ top: index * ROW_HEIGHT, height: ROW_HEIGHT }}
                  onClick={() => onStepClick?.(step)}
                />
              </TooltipTrigger>
              <TooltipContent className={cx(TOOLTIP_SURFACE, "z-[14]")}>
                <StepTooltipBody step={step} total={total} />
              </TooltipContent>
            </Tooltip>
          ))}
        </div>
      </Show>
    </Card>
  );
};
